import {
  Box,
  Button,
  MenuItem,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import type { EpocusRole } from "elephant-web/gql";
import { phone } from "phone";
import { useState } from "react";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../_lib/epocus-palette";
import { isValidNpi, NPI_ERROR_MESSAGE } from "../../_lib/npi";
import { PROGRAM_ROLE_OPTIONS, roleLabelForRole } from "./program-user-roles";

export type ProgramUserFormValues = {
  firstName: string;
  lastName: string;
  telephone: string;
  email: string;
  npi: string;
  role: EpocusRole | "";
};

type FieldErrors = Partial<Record<keyof ProgramUserFormValues, string>>;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_ERROR_MESSAGE =
  "Enter a valid mobile number in international format, e.g. +234…";

const FormRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: rem(16),
});

const FieldsRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: rem(4),
});

const FormHeading = styled(Typography)({
  fontSize: rem(14),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
});

const HelperCaption = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const toFormValues = (
  initialValues?: Partial<ProgramUserFormValues>,
): ProgramUserFormValues => ({
  firstName: initialValues?.firstName ?? "",
  lastName: initialValues?.lastName ?? "",
  telephone: initialValues?.telephone ?? "",
  email: initialValues?.email ?? "",
  npi: initialValues?.npi ?? "",
  role: initialValues?.role ?? "",
});

export const ProgramUserForm: FC<{
  initialValues?: Partial<ProgramUserFormValues>;
  submitLabel: string;
  submitting: boolean;
  onSubmit: (values: ProgramUserFormValues) => void | Promise<void>;
  emailEditable?: boolean;
  fixedRole?: EpocusRole;
  npiEditable?: boolean;
}> = ({
  initialValues,
  submitLabel,
  submitting,
  onSubmit,
  emailEditable = true,
  fixedRole,
  npiEditable = false,
}) => {
  // Initialised once from `initialValues` at mount. Callers on the edit path
  // must not render this form until the fetched user has arrived (see
  // EpocusEditUserPage), otherwise this would seed from an empty object.
  const [values, setValues] = useState<ProgramUserFormValues>(() =>
    toFormValues(
      fixedRole ? { ...initialValues, role: fixedRole } : initialValues,
    ),
  );
  const [errors, setErrors] = useState<FieldErrors>({});

  const setField = <K extends keyof ProgramUserFormValues>(
    field: K,
    value: ProgramUserFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): FieldErrors => {
    const nextErrors: FieldErrors = {};

    if (!values.firstName.trim()) {
      nextErrors.firstName = "First name is required";
    }
    if (!values.lastName.trim()) {
      nextErrors.lastName = "Last name is required";
    }
    if (!values.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!EMAIL_REGEX.test(values.email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }
    if (!values.role) {
      nextErrors.role = "Role is required";
    }
    if (values.telephone.trim() && !phone(values.telephone.trim()).isValid) {
      nextErrors.telephone = PHONE_ERROR_MESSAGE;
    }
    if (npiEditable && values.npi.trim() && !isValidNpi(values.npi)) {
      nextErrors.npi = NPI_ERROR_MESSAGE;
    }

    return nextErrors;
  };

  const handleSubmit = () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;
    void Promise.resolve(onSubmit(values)).catch(() => undefined);
  };

  return (
    <FormRoot>
      <FormHeading>User details</FormHeading>
      <FieldsRow>
        <TextField
          id="program-user-firstname"
          label="First name"
          required
          variant="outlined"
          fullWidth
          value={values.firstName}
          onChange={(e) => setField("firstName", e.target.value)}
          error={!!errors.firstName}
          helperText={errors.firstName}
        />
        <TextField
          id="program-user-lastname"
          label="Last name"
          required
          variant="outlined"
          fullWidth
          value={values.lastName}
          onChange={(e) => setField("lastName", e.target.value)}
          error={!!errors.lastName}
          helperText={errors.lastName}
        />
        <Box display="flex" flexDirection="column">
          <TextField
            id="program-user-telephone"
            label="Mobile phone number"
            placeholder="+234…"
            variant="outlined"
            fullWidth
            value={values.telephone}
            onChange={(e) => setField("telephone", e.target.value)}
            error={!!errors.telephone}
            helperText={errors.telephone}
          />
          {!errors.telephone && (
            <HelperCaption>Optional. Include the country code.</HelperCaption>
          )}
        </Box>
        <TextField
          id="program-user-email"
          label="Email"
          required
          variant="outlined"
          fullWidth
          value={values.email}
          onChange={(e) => setField("email", e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          disabled={emailEditable === false}
          InputProps={emailEditable === false ? { readOnly: true } : undefined}
        />
        {npiEditable && (
          <TextField
            id="program-user-npi"
            label="NPI"
            placeholder="10 digits"
            variant="outlined"
            fullWidth
            value={values.npi}
            onChange={(e) => setField("npi", e.target.value)}
            error={!!errors.npi}
            helperText={
              errors.npi ?? "Needed before any study they sign can be billed."
            }
            inputProps={{ inputMode: "numeric" }}
          />
        )}
        {fixedRole ? (
          <TextField
            id="program-user-role"
            label="Role"
            variant="outlined"
            fullWidth
            value={roleLabelForRole(fixedRole)}
            disabled
            InputProps={{ readOnly: true }}
          />
        ) : (
          <TextField
            id="program-user-role"
            select
            label="Role"
            required
            variant="outlined"
            fullWidth
            value={values.role}
            onChange={(e) =>
              setField("role", e.target.value as EpocusRole | "")
            }
            error={!!errors.role}
            helperText={errors.role}
          >
            {PROGRAM_ROLE_OPTIONS.map((option) => (
              <MenuItem key={option.role} value={option.role}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      </FieldsRow>
      <Button
        variant="contained"
        color="primary"
        disabled={submitting}
        onClick={handleSubmit}
        style={{ alignSelf: "flex-end" }}
      >
        {submitLabel}
      </Button>
    </FormRoot>
  );
};
